/**
 * mutualFundComparisonV2 — the pure half of the V2 fund comparison card preview
 * (`TAX_FUND_COMPARISON_V2`, refId `type15`). `MutualFundComparisonV2Preview.vue` draws what these
 * functions answer, so the rules stay testable under plain Node
 * (see tests/mutualFundComparisonV2.test.mjs).
 *
 * The preview receives one of two things and must draw both without a branch of its own:
 *  - a rendered wire payload, once a fund is picked (the server answers `PreviewCardConfig`);
 *  - the raw template, before one is picked — directives and all. That is evaluated as "no data":
 *    the card a customer gets when every guard fails, which is the honest empty state.
 *
 * COLOURS. `normalizeCard` keeps colours in their wire spelling and only `styleOf` / `boxOf` /
 * `gradientOf` / `chartModel` turn them into CSS. `hintColor` reorders `#AARRGGBB`, so running it
 * twice swaps the channels again; one conversion point is what keeps that from happening.
 *
 * Nothing in this file throws: a half-typed template in the config dialog's textarea reaches it.
 */
import { hintColor, textStyle } from './taxCardFormat.js';

/** What a cell shows when it has nothing, the same text the template's last `@else` carries. */
const NO_DATA = '-';

/** What a `{{ … }}` shows while there is no fund to fill it from. */
const UNFILLED = '…';

/** Keys that make the engine drop a node when its guard fails and no `@else` catches it. */
const DROPPED_WITHOUT_ELSE = ['@value', '@omitWhenNull', '@omitWhenZero', '@omitWhenAllZero'];

/** Longest `@else` chain followed. The template's longest is 5; this only stops a cyclic object. */
const MAX_ELSE_DEPTH = 32;

/** More placeholder columns than this is a typo: the card compares 2–3 funds. */
const MAX_PLACEHOLDERS = 12;

/** Used when the caller has not measured the plot yet. */
const DEFAULT_PLOT_WIDTH = 320;
const DEFAULT_PLOT_HEIGHT = 210;

const AXIS_TICKS = 5;

/** The y range's ends are multiples of this. */
const DOMAIN_STEP = 10;

/** Copy of the map in mutualFundPickerStyle.js, which keeps its own private. */
const FONT_WEIGHTS = { regular: 400, normal: 400, medium: 500, semibold: 600, bold: 700 };

function asObj(v) {
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
}

function asList(v) {
  return Array.isArray(v) ? v : [];
}

function has(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function isNumber(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function isRepeat(node) {
  const obj = asObj(node);
  return !!obj && has(obj, '@repeat');
}

/** A dp hint: a finite number ≥ 0, or null. A string here is a mistake the phone drops too. */
function dp(v) {
  return isNumber(v) && v >= 0 ? v : null;
}

/** A digit count `toFixed` cannot refuse. */
function decimalsOf(v, fallback) {
  return Number.isInteger(v) && v >= 0 && v <= 20 ? v : fallback;
}

/**
 * One template node evaluated with no data, following the engine's `Fill`: a failed guard answers
 * its `@else`, or drops the key when there is none.
 *
 * @param {*} node A template node or an already rendered value.
 * @returns {*} `undefined` for "no such key" (`@repeat`, or a guard with no `@else`), `'…'` for an
 *   unfilled string, otherwise the value itself. A `{th,en,zh}` bag comes back as the same object.
 */
export function previewLeaf(node) {
  let current = node;
  for (let depth = 0; depth < MAX_ELSE_DEPTH; depth += 1) {
    if (typeof current === 'string') return current.includes('{{') ? UNFILLED : current;
    const obj = asObj(current);
    if (!obj) return current;
    if (has(obj, '@repeat')) return undefined;
    if (!has(obj, '@else')) {
      return DROPPED_WITHOUT_ELSE.some((key) => has(obj, key)) ? undefined : obj;
    }
    current = obj['@else'];
  }
  return undefined;
}

/** A scalar or bag seat. `""` survives: the template's period note uses it on purpose. */
function seat(node) {
  const leaf = previewLeaf(node);
  return leaf === undefined ? null : leaf;
}

/** The objects of a list seat, with any element a failed guard would have dropped removed. */
function objectsIn(node) {
  return asList(previewLeaf(node)).map(previewLeaf).filter(asObj);
}

function normalizeFund(fund) {
  return {
    code: seat(fund.code),
    name: seat(fund.name),
    logoText: seat(fund.logoText),
    logoUrl: seat(fund.logoUrl),
    lineColor: seat(fund.lineColor),
  };
}

function normalizeFunds(node, placeholders) {
  if (!isRepeat(node)) return objectsIn(node).map(normalizeFund);
  return Array.from({ length: placeholders }, (_, i) => normalizeFund({ code: `กองที่ ${i + 1}` }));
}

function normalizeCell(cell) {
  const obj = asObj(cell) || {};
  return {
    value: previewLeaf(obj.value) ?? NO_DATA,
    best: previewLeaf(obj.best) === true,
    valueColor: seat(obj.valueColor),
  };
}

/** A non-object cell still takes its column: cells pair with `funds` by position. */
function normalizeCells(node, cellCount) {
  if (!isRepeat(node)) return asList(previewLeaf(node)).map((cell) => normalizeCell(previewLeaf(cell)));
  const template = previewLeaf(node['@template']);
  return Array.from({ length: cellCount }, () => normalizeCell(template));
}

function normalizeRows(node, cellCount) {
  return objectsIn(node).map((row) => ({
    key: seat(row.key),
    label: seat(row.label),
    labelColor: seat(row.labelColor),
    labelFontSize: seat(row.labelFontSize),
    labelFontWeight: seat(row.labelFontWeight),
    cells: normalizeCells(row.cells, cellCount),
  }));
}

function normalizePoints(node) {
  if (isRepeat(node)) return [];
  return objectsIn(node).map((point) => ({ label: seat(point.label), values: asList(point.values) }));
}

function normalizeChart(chart) {
  return {
    title: seat(chart.title),
    legendTitle: seat(chart.legendTitle),
    valueSuffix: seat(chart.valueSuffix),
    valueDecimals: seat(chart.valueDecimals),
    valueShowsPlus: seat(chart.valueShowsPlus),
    axisDecimals: seat(chart.axisDecimals),
    points: normalizePoints(chart.points),
  };
}

function normalizeTab(tab, cellCount) {
  const chart = asObj(previewLeaf(tab.chart));
  const periods = tab.periods === undefined || tab.periods === null ? null : objectsIn(tab.periods);
  return {
    key: seat(tab.key),
    label: seat(tab.label),
    selectedPeriod: seat(tab.selectedPeriod),
    periods: periods && periods.map((period) => ({
      key: seat(period.key),
      label: seat(period.label),
      // The chart tab's chips cut the series by this; a table period has none.
      count: seat(period.count),
      note: seat(period.note),
      rows: normalizeRows(period.rows, cellCount),
    })),
    rows: tab.rows === undefined || tab.rows === null ? null : normalizeRows(tab.rows, cellCount),
    chart: chart ? normalizeChart(chart) : null,
  };
}

/**
 * A wire payload's or a template's `data` as the one shape the component draws.
 *
 * @param {*} data The card's `data` object (not the envelope around it).
 * @param {number} [placeholderCount] Columns to invent when `funds` is still a `@repeat`.
 * @returns {{title: *, selectedTab: *, highlightRow: *, bestLabel: *, funds: Object[], tabs: Object[],
 *   disclaimer: *, buttonLabel: *}} Text seats may still be `{th,en,zh}` bags. An absent seat is null.
 */
export function normalizeCard(data, placeholderCount = 2) {
  const src = asObj(data) || {};
  const placeholders = Number.isInteger(placeholderCount) && placeholderCount >= 0
    ? Math.min(placeholderCount, MAX_PLACEHOLDERS)
    : 2;
  const funds = normalizeFunds(src.funds, placeholders);
  const cellCount = funds.length || placeholders;
  const button = asObj(previewLeaf(src.button));
  return {
    title: seat(src.title),
    selectedTab: seat(src.selectedTab),
    highlightRow: seat(src.highlightRow),
    bestLabel: seat(src.bestLabel),
    funds,
    tabs: objectsIn(src.tabs).map((tab) => normalizeTab(tab, cellCount)),
    disclaimer: seat(src.disclaimer),
    buttonLabel: button ? seat(button.label) : null,
  };
}

/**
 * A font weight hint as CSS.
 *
 * @param {*} v A number, or one of the names the app uses.
 * @returns {?number}
 */
export function fontWeight(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  return FONT_WEIGHTS[String(v == null ? '' : v).trim().toLowerCase()] ?? null;
}

/**
 * The text hints of one prefix. Unlike `taxCardFormat.textStyle` this also reads `FontWeight`, and
 * every key is always present, holding null when not authored.
 *
 * @param {*} data The object carrying the hints (`data`, or a row for its `label*` overrides).
 * @param {string} prefix e.g. 'title'.
 * @returns {{color: ?string, fontSize: ?string, fontWeight: ?number, textAlign: ?string}} Ready for
 *   `:style`, so `fontSize` is `'13px'`: Vue does not add the unit.
 */
export function styleOf(data, prefix) {
  const src = typeof prefix === 'string' && prefix ? asObj(data) : null;
  const text = (src && textStyle(src, prefix)) || {};
  return {
    color: text.color ?? null,
    fontSize: text.fontSize ?? null,
    fontWeight: src ? fontWeight(src[`${prefix}FontWeight`]) : null,
    textAlign: text.textAlign ?? null,
  };
}

/**
 * The box hints of one prefix as a `:style` object: `<prefix>FrameColor`, `BorderColor`,
 * `BorderWidth`, `CornerRadius`, `Padding{Top,Bottom,Left,Right}`.
 *
 * Only authored keys appear, and the border stays in longhands, so `tabSelected` (colour only) can
 * sit on top of `tab` and keep its width.
 *
 * @param {*} data
 * @param {string} prefix e.g. 'main'.
 * @returns {Object} Any of `backgroundColor`, `borderColor`, `borderWidth`, `borderStyle`,
 *   `borderRadius`, `paddingTop`, `paddingBottom`, `paddingLeft`, `paddingRight`. Never null.
 */
export function boxOf(data, prefix) {
  const src = typeof prefix === 'string' && prefix ? asObj(data) : null;
  const box = {};
  if (!src) return box;
  const frame = hintColor(src[`${prefix}FrameColor`]);
  if (frame) box.backgroundColor = frame;
  const borderColor = hintColor(src[`${prefix}BorderColor`]);
  if (borderColor) box.borderColor = borderColor;
  const borderWidth = dp(src[`${prefix}BorderWidth`]);
  if (borderWidth !== null) {
    box.borderWidth = `${borderWidth}px`;
    box.borderStyle = 'solid';
  }
  const radius = dp(src[`${prefix}CornerRadius`]);
  if (radius !== null) box.borderRadius = `${radius}px`;
  ['Top', 'Bottom', 'Left', 'Right'].forEach((side) => {
    const padding = dp(src[`${prefix}Padding${side}`]);
    if (padding !== null) box[`padding${side}`] = `${padding}px`;
  });
  return box;
}

/**
 * Several `styleOf` / `boxOf` results as one, later ones winning. A null never overwrites:
 * `styleOf` answers `fontSize: null` for a prefix that only sets a colour, and `Object.assign`,
 * `mergeStyles` or a Vue `:style` array would let that erase the base layer's size.
 *
 * @param {...*} layers
 * @returns {Object}
 */
export function layerStyles(...layers) {
  const out = {};
  layers.forEach((layer) => {
    const obj = asObj(layer);
    if (!obj) return;
    Object.keys(obj).forEach((key) => {
      if (obj[key] !== null && obj[key] !== undefined) out[key] = obj[key];
    });
  });
  return out;
}

/**
 * The strip at the top of the card, from `mainGradient{StartColor,EndColor,Height}`.
 *
 * @param {*} data
 * @returns {?{background: string, height: string}} Null unless all three hints are usable.
 */
export function gradientOf(data) {
  const src = asObj(data);
  if (!src) return null;
  const start = hintColor(src.mainGradientStartColor);
  const end = hintColor(src.mainGradientEndColor);
  const height = dp(src.mainGradientHeight);
  if (!start || !end || !height) return null;
  return { background: `linear-gradient(to bottom, ${start}, ${end})`, height: `${height}px` };
}

/**
 * One chart value as the legend prints it.
 *
 * The sign follows the printed digits, not the raw number: 0.001 at two decimals is `0.00%`, since
 * `+0.00%` and `-0.00%` both claim a direction that the reader can't see in the figure.
 *
 * @param {*} v
 * @param {{valueDecimals: number, valueShowsPlus: boolean, valueSuffix: string}} [options]
 * @returns {string} e.g. `'+29.86%'`, or `'-'` when `v` is not a finite number.
 */
export function formatChartValue(v, options) {
  if (!isNumber(v)) return NO_DATA;
  const o = asObj(options) || {};
  const fixed = v.toFixed(decimalsOf(o.valueDecimals, 2));
  const shown = Number(fixed);
  const digits = shown === 0 ? fixed.replace('-', '') : fixed;
  const plus = o.valueShowsPlus === true && shown > 0 ? '+' : '';
  const suffix = typeof o.valueSuffix === 'string' ? o.valueSuffix : '';
  return `${plus}${digits}${suffix}`;
}

/** The y range: the data rounded outward to the nearest 10, or a neutral [-1, 1] when there is nothing to fit. */
function domainOf(values) {
  if (!values.length) return { min: -1, max: 1 };
  const low = values.reduce((a, b) => Math.min(a, b));
  const high = values.reduce((a, b) => Math.max(a, b));
  // Read from one screenshot of mobile's type15 chart (5Y: 60.0 … -40.0); mobile has not confirmed this rule.
  const min = Math.floor(low / DOMAIN_STEP) * DOMAIN_STEP;
  // `+ 0`: ceil gives -0 for a value in (-10, 0), and -0 is not the 0 a caller compares with.
  const max = Math.ceil(high / DOMAIN_STEP) * DOMAIN_STEP + 0;
  // A value already on a multiple of 10 rounds to itself, and a zero span would divide by zero below.
  return min === max ? { min: min - DOMAIN_STEP, max: max + DOMAIN_STEP } : { min, max };
}

/**
 * The chart's geometry, in plot coordinates: x runs 0…width, y runs 0 (top) …height (bottom). The
 * caller offsets it for padding and axis labels.
 *
 * @param {Object} options
 * @param {Array} options.points `{label, values}`, oldest first; `values` pair with `funds` by position.
 * @param {Array} options.funds One series is drawn per fund; `lineColor` becomes its `color`.
 * @param {number} [options.count] Keep only the last N points. Absent or not positive ⇒ all of them.
 * @param {number} [options.selectedIndex] Index into the KEPT points. Absent ⇒ last; out of range ⇒ clamped.
 * @param {number} [options.width]
 * @param {number} [options.height]
 * @param {number} [options.axisDecimals]
 * @returns {{points: Array, xs: number[],
 *   ys: {min: number, max: number, ticks: Array<{value: number, label: string, y: number}>},
 *   series: Array<{index: number, color: ?string, segments: Array<Array<{x: number, y: number}>>,
 *     dots: Array<{x: number, y: number}>}>,
 *   guideX: ?number, selectedIndex: ?number}} `ticks` run low to high, evenly spaced. A fund with
 *   no values still has its series entry, with empty `segments` and `dots`. `guideX` and
 *   `selectedIndex` are null when there are no points.
 */
export function chartModel(options) {
  const o = asObj(options) || {};
  const width = isNumber(o.width) && o.width > 0 ? o.width : DEFAULT_PLOT_WIDTH;
  const height = isNumber(o.height) && o.height > 0 ? o.height : DEFAULT_PLOT_HEIGHT;
  const axisDecimals = decimalsOf(o.axisDecimals, 1);
  const funds = asList(o.funds);
  const every = asList(o.points).filter(asObj);
  // `slice(-0)` is the whole list, so a count of 0 must not reach it as a count.
  const points = Number.isInteger(o.count) && o.count > 0 ? every.slice(-o.count) : every;
  const last = points.length - 1;

  const valueAt = (point, fundIndex) => {
    const v = asList(point.values)[fundIndex];
    return isNumber(v) ? v : null;
  };

  const known = [];
  funds.forEach((_, fundIndex) => {
    points.forEach((point) => {
      const v = valueAt(point, fundIndex);
      if (v !== null) known.push(v);
    });
  });
  const { min, max } = domainOf(known);
  const yOf = (v) => height - ((v - min) / (max - min)) * height;

  const xs = points.map((_, i) => (last > 0 ? (i * width) / last : width / 2));

  const ticks = Array.from({ length: AXIS_TICKS }, (_, i) => {
    const share = i / (AXIS_TICKS - 1);
    // `+ 0` turns -0 into 0, which would otherwise print as "-0.0".
    const value = Number((min + (max - min) * share).toFixed(axisDecimals)) + 0;
    // Positioned by the evenly spaced share, not the rounded value: on a narrow domain rounding
    // would push a gridline outside the plot.
    return { value, label: value.toFixed(axisDecimals), y: height - height * share };
  });

  const series = funds.map((fund, index) => {
    const segments = [];
    const dots = [];
    let run = null;
    points.forEach((point, i) => {
      const v = valueAt(point, index);
      if (v === null) {
        run = null;
        return;
      }
      const at = { x: xs[i], y: yOf(v) };
      dots.push(at);
      if (!run) {
        run = [];
        segments.push(run);
      }
      run.push(at);
    });
    return { index, color: hintColor(asObj(fund)?.lineColor), segments, dots };
  });

  let selectedIndex = null;
  if (points.length) {
    selectedIndex = isNumber(o.selectedIndex)
      ? Math.min(Math.max(Math.trunc(o.selectedIndex), 0), last)
      : last;
  }

  return {
    points,
    xs,
    ys: { min, max, ticks },
    series,
    guideX: selectedIndex === null ? null : xs[selectedIndex],
    selectedIndex,
  };
}

/**
 * The legend's heading: `{label}` is the mobile app's own placeholder, not template syntax.
 *
 * `split`/`join` rather than `replace`, so a `$` in the label is not read as a replacement pattern.
 *
 * @param {*} template One language's string. A `{th,en,zh}` bag must be resolved by the caller.
 * @param {*} label
 * @returns {string} `''` when `template` is not a string.
 */
export function legendTitle(template, label) {
  if (typeof template !== 'string') return '';
  return template.split('{label}').join(label === null || label === undefined ? '' : String(label));
}
